import React from 'react';
import { ToastMessage } from '@cokitchen/cokitchen-components';
import { toast } from 'react-toastify';
import { PostType } from 'models/posts.model';
import moment from 'moment';
import { ENUM_AM_PM } from './enums';
import { DealType } from 'models/deals.model';
import { isBefore, parse, isSameDay as dateFnsIsSameDay, set, isAfter } from 'date-fns';
import { MultiselectItem } from '@cokitchen/cokitchen-components/dist/form/multiselect/types';
import { MealType } from 'models/meal.model';
import { BrandType } from 'models/brand.model';

export const handleApiError = (error: unknown, fallback = 'An error occurred') => {
  const message = (error as any)?.response?.data?.message;
  const messages = Array.isArray(message) ? message : [message];
  messages.forEach((message) => {
    toast(<ToastMessage type='error' message={message || fallback} />);
  });
};

export const filterActivePosts = (posts: PostType[]) => {
  if (!posts) return [];
  return posts.filter((post) => isPostActive(post));
};

export const filterInactivePosts = (posts: PostType[]) => {
  if (!posts) return [];
  return posts.filter((post) => {
    const startDateTime = moment(
      `${post.to_start_date} ${post.to_start_time}`,
      'YYYY-MM-DD HH:mm:ss'
    );

    const expireDateTime = post.to_expire_date
      ? moment(`${post.to_expire_date} ${post.to_expire_time || '23:59:59'}`, 'YYYY-MM-DD HH:mm:ss')
      : null;

    const now = moment();

    if (expireDateTime) {
      return now.isBefore(startDateTime) || now.isAfter(expireDateTime);
    } else {
      return now.isBefore(startDateTime);
    }
  });
};

export const isPostActive = (post: PostType) => {
  const startDateTime = moment(
    `${post.to_start_date} ${post.to_start_time}`,
    'YYYY-MM-DD HH:mm:ss'
  );
  let expireDateTime;
  const now = moment();

  if (post.to_expire_date && post.to_expire_time) {
    expireDateTime = moment(`${post.to_expire_date} ${post.to_expire_time}`, 'YYYY-MM-DD HH:mm:ss');
  } else if (post.to_expire_date) {
    expireDateTime = moment(post.to_expire_date, 'YYYY-MM-DD');
  } else {
    expireDateTime = null;
  }

  if (startDateTime && expireDateTime) return now.isBetween(startDateTime, expireDateTime);
  if (startDateTime) return now.isSameOrAfter(startDateTime);
  return false;
};

export const convertTimeString = (timeString: string) => {
  if (!timeString) return null;
  const [hours, minutes] = timeString.split(':');
  const totalHours = parseInt(hours, 10);
  const amPm = totalHours >= 12 ? ENUM_AM_PM.PM : ENUM_AM_PM.AM;
  const convertedHours = totalHours % 12 === 0 ? 12 : totalHours % 12;
  const formattedHours = String(convertedHours).padStart(2, '0');
  const formattedMinutes = String(minutes).padStart(2, '0');

  return {
    hours: formattedHours,
    minutes: formattedMinutes,
    amPm,
    totalHours: String(totalHours).padStart(2, '0'),
    timeWithColon: `${totalHours.toString().padStart(2, '0')}:${formattedMinutes}`
  };
};

export const padArray = (arr: string[], requiredLength: number) => {
  while (arr.length < requiredLength) {
    arr.push('');
  }

  return arr;
};

export const filterActiveDeals = (deals: DealType[]) => {
  return deals.filter((deal) => isDealActive(deal));
};

export const filterInactiveDeals = (deals: DealType[]) => {
  return deals.filter((deal) => {
    const startDateTime = moment(
      `${deal.to_start_date} ${deal.to_start_time}`,
      'YYYY-MM-DD HH:mm:ss'
    );

    const expireDateTime = deal.to_expire_date
      ? moment(`${deal.to_expire_date} ${deal.to_expire_time || '23:59:59'}`, 'YYYY-MM-DD HH:mm:ss')
      : null;

    const now = moment();

    if (expireDateTime) {
      return now.isBefore(startDateTime) || now.isAfter(expireDateTime);
    } else {
      return now.isBefore(startDateTime);
    }
  });
};

export const isDealActive = (deal: DealType) => {
  const startDateTime = moment(
    `${deal.to_start_date} ${deal.to_start_time}`,
    'YYYY-MM-DD HH:mm:ss'
  );
  let expireDateTime;
  const now = moment();

  if (deal.to_expire_date && deal.to_expire_time) {
    expireDateTime = moment(`${deal.to_expire_date} ${deal.to_expire_time}`, 'YYYY-MM-DD HH:mm:ss');
  } else if (deal.to_expire_date) {
    expireDateTime = moment(deal.to_expire_date, 'YYYY-MM-DD');
  } else {
    expireDateTime = null;
  }

  if (startDateTime && expireDateTime) return now.isBetween(startDateTime, expireDateTime);
  if (startDateTime) return now.isSameOrAfter(startDateTime);
  return false;
};

export const isSameDay = (date1: Date | undefined, date2: Date | undefined) => {
  if (!date1 || !date2) return false;
  return dateFnsIsSameDay(date1, date2);
};

export const isTodayAndTimePassed = (date: Date | undefined, time: string | undefined) => {
  if (!date || !time) return false;
  const currentDate = new Date();
  if (dateFnsIsSameDay(date, currentDate)) {
    const [hours, minutes] = time.split(':').map(Number);
    const timeWithDate = set(currentDate, { hours, minutes, seconds: 0, milliseconds: 0 });
    return isAfter(currentDate, timeWithDate);
  }
  return false;
};

export const isEndTimeBeforeStartTime = (
  startTime: string | undefined,
  endTime: string | undefined
) => {
  if (!startTime || !endTime) return false;
  const start = parse(startTime, 'HH:mm', new Date());
  const end = parse(endTime, 'HH:mm', new Date());
  return isBefore(end, start);
};

export const generateKey = (items: MultiselectItem[]): string => {
  return items.map((item) => `${item.text}-${item.value}`).join('|');
};

export const getCokitchenName = (meal: MealType, brandData: BrandType[]) => {
  const brandId = meal?.brand_id;
  const brand = brandData.find((b) => b.id === brandId);
  if (brandId && brand) return brand?.cokitchen?.name;
  return '';
};
